import { safeLocalStorage, safeSessionStorage, safeRandomUUID } from "./safeStorage";
import { UserProfile } from "../types";

/**
 * Client-Side Session & Anonymous Device Scoping Module
 * Provides a resilient multi-vault pseudonymous device identifier stored across browser
 * localStorage, sessionStorage, cookies, and IndexedDB to organize, namespace,
 * and isolate local scan history items per browser instance without requiring account login.
 */

const DEVICE_ID_KEY = "catchai_device_id_v2";
const LEGACY_DEVICE_ID_KEY = "veritext_device_id_v2";
const LEGACY_SESSION_KEY = "catchai_user_session_id";
const OLD_VERITEXT_SESSION_KEY = "veritext_user_session_id";
const PROFILE_STORAGE_KEY = "catchai_user_profile_v2";
const LEGACY_PROFILE_KEY = "veritext_user_profile_v2";
const ANCIENT_PROFILE_KEY = "veritext_user_profile";

const AVATAR_GRADIENTS = [
  "from-indigo-600 to-violet-500",
  "from-blue-600 to-cyan-500",
  "from-emerald-600 to-teal-500",
  "from-purple-600 to-pink-500",
  "from-amber-600 to-orange-500",
  "from-rose-600 to-red-500"
];

function selectDeterministicGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[index];
}

function getDeviceSummary(): string {
  if (typeof window === "undefined" || !navigator) return "Academic Terminal";
  const ua = navigator.userAgent;
  let os = "Desktop";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Macintosh|Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";

  let browser = "Browser";
  if (/Edg/i.test(ua)) browser = "Edge";
  else if (/Chrome/i.test(ua)) browser = "Chrome";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Safari/i.test(ua)) browser = "Safari";

  return `${browser} on ${os}`;
}

/**
 * Retrieves or initializes the active device session ID for browser-local history record tagging.
 * Coordinates with the multi-vault device fingerprint engine.
 */
export function getUserSessionId(): string {
  if (typeof window === "undefined") return "server-session";
  
  // Check primary device ID key first, then legacy session keys
  let deviceId =
    safeLocalStorage.getItem(DEVICE_ID_KEY) ||
    safeLocalStorage.getItem(LEGACY_DEVICE_ID_KEY) ||
    safeSessionStorage.getItem(DEVICE_ID_KEY) ||
    safeSessionStorage.getItem(LEGACY_DEVICE_ID_KEY) ||
    safeLocalStorage.getItem(LEGACY_SESSION_KEY) ||
    safeLocalStorage.getItem(OLD_VERITEXT_SESSION_KEY);

  if (!deviceId) {
    deviceId = "dev_" + safeRandomUUID().replace(/-/g, "").slice(0, 24);
  }

  // Ensure uniform persistence across active keys
  safeLocalStorage.setItem(DEVICE_ID_KEY, deviceId);
  safeSessionStorage.setItem(DEVICE_ID_KEY, deviceId);

  return deviceId;
}

/**
 * Retrieves or creates the current user's academic profile.
 */
export function getUserProfile(): UserProfile {
  const currentSessionId = getUserSessionId();
  const raw =
    safeLocalStorage.getItem(PROFILE_STORAGE_KEY) ||
    safeLocalStorage.getItem(LEGACY_PROFILE_KEY) ||
    safeLocalStorage.getItem(ANCIENT_PROFILE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as UserProfile;
      if (parsed && parsed.id && parsed.shortId) {
        // Upgrade legacy VTX shortId if present
        if (parsed.shortId.startsWith("VTX-")) {
          parsed.shortId = parsed.shortId.replace("VTX-", "CAT-");
          safeLocalStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(parsed));
        }
        return parsed;
      }
    } catch {
      // Fallback to fresh profile creation below
    }
  }

  const shortSuffix = currentSessionId.replace(/^dev_|^usr_/, "").slice(0, 4).toUpperCase() || "8F3A";
  const shortId = `CAT-USR-${shortSuffix}`;
  const seed = currentSessionId;
  const newProfile: UserProfile = {
    id: currentSessionId,
    shortId,
    displayName: `Scholar #${shortSuffix}`,
    role: "Academic Investigator",
    affiliation: "Independent Research",
    avatarSeed: seed,
    avatarGradient: selectDeterministicGradient(seed),
    createdAt: new Date().toISOString(),
    lastActiveAt: new Date().toISOString(),
    totalScansCount: 0,
    deviceSummary: getDeviceSummary(),
    recoveryToken: "cat_rec_" + safeRandomUUID().replace(/-/g, "").slice(0, 16)
  };

  safeLocalStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(newProfile));
  return newProfile;
}

/**
 * Checks whether a local history item is associated with the current browser session ID.
 * Supports legacy usr_ and current dev_ identifiers.
 */
export function verifyDataOwnership(item: { ownerId?: string } | null | undefined): boolean {
  if (!item) return false;
  const currentSessionId = getUserSessionId();

  // If item has an explicit ownerId, verify it matches current user session or legacy key
  if (item.ownerId) {
    if (item.ownerId === currentSessionId) return true;
    const legacyKey = safeLocalStorage.getItem(LEGACY_SESSION_KEY);
    if (legacyKey && item.ownerId === legacyKey) {
      // Re-bind to current deviceId
      item.ownerId = currentSessionId;
      return true;
    }
    return false;
  }

  // Claim unowned session records for the current session owner
  item.ownerId = currentSessionId;
  return true;
}

/**
 * Filters a list of local history records, returning items matching the current browser session ID.
 */
export function filterOwnedData<T extends { ownerId?: string }>(items: T[]): T[] {
  if (!Array.isArray(items)) return [];
  const currentSessionId = getUserSessionId();
  const legacyKey = safeLocalStorage.getItem(LEGACY_SESSION_KEY);

  return items.filter((item) => {
    if (!item) return false;
    if (!item.ownerId) {
      item.ownerId = currentSessionId;
      return true;
    }
    if (item.ownerId === currentSessionId) return true;
    if (legacyKey && item.ownerId === legacyKey) {
      item.ownerId = currentSessionId;
      return true;
    }
    return false;
  });
}


